import { isNil } from "lodash";
import { useTranslation } from "react-i18next";

import {
  createErrorResultWithNoErrorDetails,
  createSuccessResult,
  DialogType,
  IRideDto,
  Log,
  RideStatusEnum,
  SimpleResult,
  useDialogs,
  VehicleTypeApi,
} from "@ryde/shared";

enum RideDtoAdditionalCheckErrorReason {
  DraftRidePassengersBiggerThenVehicleType = "DraftRidePassengersBiggerThenVehicleType",
}

const checkDraftRidePassengersBiggerThenVehicleType = async ({
  rideDto,
  vehicleTypeId,
}: {
  rideDto: Partial<IRideDto>;
  vehicleTypeId?: number;
}): Promise<SimpleResult<RideDtoAdditionalCheckErrorReason>> => {
  const { metadata, status } = rideDto;
  if (status === RideStatusEnum.Draft && !isNil(vehicleTypeId)) {
    const vehicleType = await VehicleTypeApi.getOne(vehicleTypeId);

    if (isNil(vehicleType?.maxPassengers)) {
      Log.errorAndToast("No max passengers in vehicle type, can't check");
      return createSuccessResult();
    }

    if (isNil(metadata?.seatsCount)) {
      Log.errorAndToast("No passengers count in ride, can't check");
      return createSuccessResult();
    }

    if (vehicleType.maxPassengers < metadata.seatsCount) {
      return new SimpleResult({
        success: false,
        data: RideDtoAdditionalCheckErrorReason.DraftRidePassengersBiggerThenVehicleType,
      });
    }
  }

  return createSuccessResult();
};

export const useRideAdditionalCheck = () => {
  const { t } = useTranslation();
  const { openAsyncDialog } = useDialogs();

  // if not success ride shouldn't be saved
  const checkAndResolveRideDto = async ({
    rideDto,
    formValuesSnapshot,
  }: {
    rideDto: Partial<IRideDto>;
    formValuesSnapshot?: Partial<IRideDto>;
  }): Promise<SimpleResult<Partial<IRideDto>>> => {
    const checkResult = await checkDraftRidePassengersBiggerThenVehicleType({
      rideDto,
      vehicleTypeId: formValuesSnapshot?.vehicleTypeId,
    });

    if (!checkResult.success) {
      if (
        checkResult.data ===
        RideDtoAdditionalCheckErrorReason.DraftRidePassengersBiggerThenVehicleType
      ) {
        const confirmationResult = await openAsyncDialog({
          dialogName: DialogType.RideConfirm,
          dialogProps: {
            title: t("general.CONFIRM"),
            message: t("ride.YOU_TRY_TO_ADD_VEHICLE_TYPE_WITH_LESS_SEATS"),
            okButtonLabel: t("ride.MOVE_TO_DRAFT"),
          },
        });

        if (confirmationResult.success) {
          return createSuccessResult({ status: RideStatusEnum.Draft });
        }

        return createErrorResultWithNoErrorDetails();
      }
    }

    return createSuccessResult({});
  };

  return { checkAndResolveRideDto };
};
